/**
 * The analyze page's "read from a link / text / screenshot" card: what
 * the intake's answer means and where it leads. Pure, so the card's
 * rules are unit-tested (tests/unit/intake-outcome.test.mjs) and the
 * bot or the extension can read an answer the same way.
 *
 * The backend's status is one word ("valued" | "missing" | "unsupported");
 * the card says precisely what was read and what follows (owner,
 * 2026-10-25: "what exactly is missing is not mentioned"):
 *
 *   valued       a complete sale flat, the verdict follows the card
 *   missing      a sale flat short of a field: the calculator, prefilled
 *   ask          the deal or the type was not read: one tap each
 *   rent_flat    a flat for rent: the calculator's rent side, prefilled
 *   house_sale / house_rent: the house calculator, prefilled
 *   daily_rent / land / commercial: not valued yet; the facts stay and
 *                the listings of that kind are one tap away
 */
import type { IntakeAnalyzeResponse, IntakeFields, IntakeResult } from "@/types";
import type { TranslationKey } from "@/lib/i18n/keys";

export type IntakeCase =
  | "valued" | "missing" | "ask" | "rent_flat" | "house_sale" | "house_rent"
  | "daily_rent" | "land" | "commercial";

/** The two things the card asks for when the post did not say them. */
export function unknownParts(f: IntakeFields): { deal: boolean; type: boolean } {
  return {
    deal: !(f.deal === "sale" || f.deal === "rent" || f.deal === "daily_rent"),
    type: !(f.property_type === "apartment" || f.property_type === "house"
      || f.property_type === "land" || f.property_type === "commercial"),
  };
}

export function intakeCase(data: IntakeAnalyzeResponse): IntakeCase {
  const f = data.intake.fields;
  if (data.status === "valued") return "valued";
  // Land and a commercial space are not valued whatever the deal; a
  // daily rental is not valued whatever the type.
  if (f.property_type === "land") return "land";
  if (f.property_type === "commercial") return "commercial";
  if (f.deal === "daily_rent") return "daily_rent";
  const unknown = unknownParts(f);
  if (unknown.deal || unknown.type) return "ask";
  if (f.property_type === "house") return f.deal === "rent" ? "house_rent" : "house_sale";
  if (f.deal === "rent") return "rent_flat";
  return "missing";
}

/** The scene beside the status line (public/illustrations/intake). */
export const INTAKE_ART: Record<IntakeCase | "none", "reading" | "unsupported" | "missing" | "none"> = {
  valued: "reading", missing: "missing", ask: "missing",
  rent_flat: "unsupported", house_sale: "unsupported", house_rent: "unsupported",
  daily_rent: "unsupported", land: "unsupported", commercial: "unsupported",
  none: "none",
};

export const INTAKE_FIELD_LABEL: Record<string, TranslationKey> = {
  price: "intake.f.price", room_count: "intake.f.rooms", area_m2: "intake.f.area",
  floor: "intake.f.floor", total_floors: "intake.f.floors", location: "intake.f.place",
  land_area_sot: "intake.f.land", deal: "intake.u.deal", property_type: "intake.u.type",
};

/** The calculator, prefilled with what was read (its ?prefill keys:
 *  type, area, rooms, floor, floors, land_sot, building, repair, deed,
 *  lat/lng/label/cslug; deal=rent opens its result on the rent side). */
export function calculatorHref(r: IntakeResult, c?: IntakeCase): string {
  const q = new URLSearchParams({ src: "intake" });
  const f = r.fields;
  const house = f.property_type === "house" || c === "house_sale" || c === "house_rent";
  if (house || f.property_type === "apartment") q.set("type", house ? "house" : "apartment");
  if (f.area_m2) q.set("area", String(f.area_m2));
  if (f.room_count) q.set("rooms", String(f.room_count));
  if (house) {
    if (f.land_area_sot) q.set("land_sot", String(f.land_area_sot));
  } else {
    if (f.floor) q.set("floor", String(f.floor));
    if (f.total_floors) q.set("floors", String(f.total_floors));
    if (f.building === "new" || f.building === "old") q.set("building", f.building);
  }
  if (typeof f.has_repair === "boolean") q.set("repair", f.has_repair ? "1" : "0");
  if (typeof f.has_bill_of_sale === "boolean") q.set("deed", f.has_bill_of_sale ? "1" : "0");
  if (f.deal === "rent" || c === "rent_flat" || c === "house_rent") q.set("deal", "rent");
  const point = r.residence?.lat && r.residence?.lng ? r.residence : (r.place?.lat && r.place?.lng ? r.place : null);
  if (point && point.lat && point.lng) {
    q.set("lat", String(point.lat));
    q.set("lng", String(point.lng));
    q.set("label", r.residence?.name || r.place?.name || "");
    if (r.residence?.cluster_id) q.set("cslug", r.residence.cluster_id);
  }
  return `/calculate?${q.toString()}`;
}

/** The listings of the kind we do not value yet, so the read is no dead
 *  end; null where the calculator is the next step. */
export function listingsHref(c: IntakeCase): string | null {
  switch (c) {
    case "land": return "/listings?category=land";
    case "commercial": return "/listings?category=commercial";
    case "daily_rent": return "/listings?leased=true";
    default: return null;
  }
}

/** What a listing of this kind states that the read did not catch, in
 *  the card's field order: the "could not read" line. The deal and the
 *  type come first since everything else depends on them. */
export function unreadFields(r: IntakeResult): string[] {
  const f = r.fields;
  const unknown = unknownParts(f);
  const type = f.property_type;
  const out: string[] = [];
  if (unknown.deal) out.push("deal");
  if (unknown.type) out.push("property_type");
  if (r.price_azn == null) out.push("price");
  if (type !== "land" && type !== "commercial" && f.room_count == null) out.push("room_count");
  if (type !== "land" && f.area_m2 == null) out.push("area_m2");
  if ((type === "house" || type === "land") && f.land_area_sot == null) out.push("land_area_sot");
  if ((type === "apartment" || unknown.type) && f.floor == null) out.push("floor");
  if ((type === "apartment" || unknown.type) && f.total_floors == null) out.push("total_floors");
  if (!r.residence && !r.place) out.push("location");
  return out;
}

/** The seller's own words as the reader found them (the page's
 *  description, the post itself), cut at a word before `max`
 *  characters; null when none was read. Never the page's text: its
 *  title, menus and price box are no description (owner, 2026-10-27). */
export function excerpt(r: IntakeResult, max = 160): string | null {
  const text = (r.description || "").replace(/\s+/g, " ").trim();
  if (!text) return null;
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const at = cut.lastIndexOf(" ");
  return `${(at > max * 0.6 ? cut.slice(0, at) : cut).replace(/[\s,;:.]+$/, "")}...`;
}

/** Price per m² of a sale, when both the price and the area were read. */
export function pricePerM2(r: IntakeResult): number | null {
  const f = r.fields;
  if (f.deal === "rent" || f.deal === "daily_rent") return null;
  if (!r.price_azn || !f.area_m2 || f.area_m2 <= 0) return null;
  return Math.round(r.price_azn / f.area_m2);
}

/** "25 oktyabr" from the ISO day the page stated; null when it did not. */
export function listedAtMs(r: IntakeResult): number | null {
  const s = r.listed_at;
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const ms = Date.parse(`${s}T12:00:00`);
  return Number.isFinite(ms) ? ms : null;
}
